/**
 * useIsMobile — iter 182
 *
 * Combined mobile detection:
 *   1) User-Agent detection (true mobile devices like iOS/Android — survives tablet portrait modes
 *      and is reliable even when window is resized).
 *   2) Viewport width detection (matchMedia listener at the configured breakpoint).
 *
 * Returns an object so consumers can pick exactly what they need:
 *   { isMobile, isMobileDevice, isNarrowViewport, isTouchDevice }
 *
 * Components can pick:
 *   - `isMobile`           → true if either UA says mobile OR viewport is narrow (most common)
 *   - `isMobileDevice`     → strict UA-based check (iOS/Android phones, mobile UA tokens)
 *   - `isNarrowViewport`   → viewport-only check at the given breakpoint (default 768px = Tailwind md)
 *   - `isTouchDevice`      → ANY touch-capable device incl. tablets
 *
 * On the very first render we already have the correct value (SSR-safe defaults to false).
 */
import { useEffect, useState } from 'react';

const MOBILE_UA_REGEX = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i;
const TABLET_UA_REGEX = /iPad|Android(?!.*Mobile)/i;

function detectMobileDevice() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (MOBILE_UA_REGEX.test(ua)) return true;
  // iPadOS 13+ pretends to be Mac — detect via touch
  if (TABLET_UA_REGEX.test(ua)) return true;
  if (
    /Macintosh/.test(ua) &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1
  ) {
    return true;
  }
  return false;
}

function detectTouchDevice() {
  if (typeof window === 'undefined') return false;
  return (
    'ontouchstart' in window ||
    (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0)
  );
}

export default function useIsMobile(breakpointPx = 768) {
  const getInitial = () => {
    if (typeof window === 'undefined') {
      return {
        isMobileDevice: false,
        isNarrowViewport: false,
        isTouchDevice: false,
        isMobile: false,
      };
    }
    const dev = detectMobileDevice();
    const narrow = window.innerWidth < breakpointPx;
    const touch = detectTouchDevice();
    return {
      isMobileDevice: dev,
      isNarrowViewport: narrow,
      isTouchDevice: touch,
      isMobile: dev || narrow,
    };
  };

  const [state, setState] = useState(getInitial);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(`(max-width: ${breakpointPx - 1}px)`);
    const handler = () => {
      const dev = detectMobileDevice();
      const narrow = mql.matches;
      const touch = detectTouchDevice();
      setState({
        isMobileDevice: dev,
        isNarrowViewport: narrow,
        isTouchDevice: touch,
        isMobile: dev || narrow,
      });
    };
    // initial sync (in case UA-detection changed since mount)
    handler();
    if (mql.addEventListener) {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    // fallback for older browsers
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, [breakpointPx]);

  return state;
}

// Named exports for situational use
export { detectMobileDevice, detectTouchDevice };
